/**
 * Radar Chart for React-PDF
 *
 * Generates a radar chart using react-pdf's native Svg components
 * instead of an SVG string, which react-pdf's Image component doesn't support.
 */

import { Svg, G, Path, Circle, Text as SvgText } from "@react-pdf/renderer";
import * as React from "react";
import type { ResultSegment } from "../types";

interface RadarChartPdfOptions {
  /** Base width for viewBox calculation (default: 400) */
  width?: number;
  /** Base height for viewBox calculation (default: 400) */
  height?: number;
  /** Margin for labels (default: 80) */
  margin?: number;
  /** Primary data color (default: #2563eb) */
  primaryColor?: string;
  /** Secondary/comparison data color (default: #93c5fd) */
  secondaryColor?: string;
  /** Grid lines color (default: #d1d5db) */
  gridColor?: string;
  /** Label text color (default: #374151) */
  textColor?: string;
  /** Label font size (default: 7) */
  fontSize?: number;
  /** Show dimension labels (default: true) */
  showLabels?: boolean;
  /** Show percentage values (default: true) */
  showValues?: boolean;
  /**
   * Responsive mode: use 100% width to fill parent container (default: true)
   * When true, the radar fills its parent container
   * When false, uses fixed pixel dimensions from width/height
   */
  responsive?: boolean;
}

/**
 * Generate path data for a radar chart polygon
 * @param values
 * @param centerX
 * @param centerY
 * @param radius
 */
function generateRadarPathData(values: number[], centerX: number, centerY: number, radius: number): string {
  // Guard against empty array (would cause Infinity)
  if (values.length === 0) return "";

  const angleStep = (2 * Math.PI) / values.length;
  const points = values.map((value, index) => {
    const angle = index * angleStep - Math.PI / 2; // Start from top
    const normalizedValue = value / 100; // Assuming 0-100 scale
    const x = centerX + radius * normalizedValue * Math.cos(angle);
    const y = centerY + radius * normalizedValue * Math.sin(angle);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });

  return `M ${points.join(" L ")} Z`;
}

/**
 * Generate grid line paths for radar chart
 * @param centerX
 * @param centerY
 * @param radius
 * @param segments
 * @param levels
 */
function generateGridPathData(
  centerX: number,
  centerY: number,
  radius: number,
  segments: number,
  levels: number
): string[] {
  // Guard against zero segments (would cause Infinity)
  if (segments === 0) return [];

  const paths: string[] = [];
  const angleStep = (2 * Math.PI) / segments;

  // Axis lines from center to each vertex
  for (let index = 0; index < segments; index++) {
    const angle = index * angleStep - Math.PI / 2;
    const x = centerX + radius * Math.cos(angle);
    const y = centerY + radius * Math.sin(angle);
    paths.push(`M ${centerX},${centerY} L ${x.toFixed(2)},${y.toFixed(2)}`);
  }

  // Circular grid levels (polygons)
  for (let level = 1; level <= levels; level++) {
    const levelRadius = (radius * level) / levels;
    const levelPoints = [];
    for (let index = 0; index < segments; index++) {
      const angle = index * angleStep - Math.PI / 2;
      const x = centerX + levelRadius * Math.cos(angle);
      const y = centerY + levelRadius * Math.sin(angle);
      levelPoints.push(`${x.toFixed(2)},${y.toFixed(2)}`);
    }
    paths.push(`M ${levelPoints.join(" L ")} Z`);
  }

  return paths;
}

/**
 * Wrap text to fit within a character limit
 * @param text
 * @param maxChars
 */
function wrapText(text: string, maxChars: number): string[] {
  if (text.length <= maxChars) return [text];

  const words = text.split(" ");
  const lines: string[] = [];
  let currentLine = "";

  for (const word of words) {
    if ((currentLine + " " + word).trim().length <= maxChars) {
      currentLine = (currentLine + " " + word).trim();
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  }
  if (currentLine) lines.push(currentLine);

  return lines;
}

/**
 * Generate radar chart as React-PDF Svg component
 *
 * This creates a native react-pdf Svg element that renders properly
 * in generated PDFs, unlike Image with SVG data URIs.
 * @param root0
 * @param root0.data
 * @param root0.options
 */
export function RadarChartPdf({
  data,
  options = {}
}: {
  data: ResultSegment[];
  options?: RadarChartPdfOptions;
}): React.ReactElement {
  // Ensure minimum size to prevent calculation errors
  const minSize = 150;
  const {
    width: rawWidth = 350,
    height: rawHeight = 350,
    primaryColor = "#2563eb",
    secondaryColor = "#93c5fd",
    gridColor = "#d1d5db",
    textColor = "#374151",
    fontSize = 7,
    showLabels = true,
    showValues = true,
    responsive = false // Fixed dimensions by default for predictable PDF layout
  } = options;

  // Apply minimum size constraint
  const width = Math.max(rawWidth, minSize);
  const height = Math.max(rawHeight, minSize);

  // Scale margin proportionally to size (default 70 for 350px = 20% of size)
  const margin = Math.min(width * 0.2, height * 0.2);

  // Guard: Return placeholder if no data
  if (!data || data.length === 0) {
    return React.createElement(
      Svg,
      { width: width, height: height, viewBox: `0 0 ${width} ${height}` },
      React.createElement(
        SvgText,
        {
          x: width / 2,
          y: height / 2,
          textAnchor: "middle",
          style: { fontSize: 10, fill: "#999" }
        },
        "[Sem dados para exibir]"
      )
    );
  }

  // Add padding to viewBox to prevent text clipping
  const viewBoxPadding = 40;
  const viewBoxWidth = width + viewBoxPadding * 2;
  const viewBoxHeight = height + viewBoxPadding * 2;

  const centerX = viewBoxWidth / 2;
  const centerY = viewBoxHeight / 2;
  const radius = Math.max(Math.min(width, height) / 2 - margin, 10); // Ensure minimum radius

  // Debug: Log all calculated values to find Infinity
  console.log("[RadarChartPdf] Dimensions:", {
    width,
    height,
    margin,
    viewBoxWidth,
    viewBoxHeight,
    centerX,
    centerY,
    radius,
    dataLength: data.length
  });

  // Final guard: check for any Infinity or NaN values
  const allValues = [width, height, margin, viewBoxWidth, viewBoxHeight, centerX, centerY, radius];
  const hasInvalidValue = allValues.some((v) => !Number.isFinite(v));
  if (hasInvalidValue) {
    console.error("[RadarChartPdf] Invalid value detected:", {
      width,
      height,
      margin,
      viewBoxWidth,
      viewBoxHeight,
      centerX,
      centerY,
      radius
    });
    return React.createElement(
      Svg,
      { width: 200, height: 200, viewBox: "0 0 200 200" },
      React.createElement(
        SvgText,
        {
          x: 100,
          y: 100,
          textAnchor: "middle",
          style: { fontSize: 10, fill: "#f00" }
        },
        "[Erro: valor inválido]"
      )
    );
  }

  const values = data.map((d) => d.value);
  const autoAvaliacaoValues = data.map((d) => d.autoAvaliacao ?? 0);
  const labels = data.map((d) => d.segment);

  // Generate grid paths
  const gridPaths = generateGridPathData(centerX, centerY, radius, data.length, 4);

  // Generate data paths
  const primaryPathData = generateRadarPathData(values, centerX, centerY, radius);
  const hasAutoAvaliacao = data.some((d) => d.autoAvaliacao !== undefined);
  const secondaryPathData = hasAutoAvaliacao
    ? generateRadarPathData(autoAvaliacaoValues, centerX, centerY, radius)
    : "";

  // Calculate label positions
  const angleStep = (2 * Math.PI) / data.length;
  const labelPositions = labels.map((label, index) => {
    const angle = index * angleStep - Math.PI / 2;
    const labelRadius = radius + 15; // Distance from radar edge to label
    const x = centerX + labelRadius * Math.cos(angle);
    const y = centerY + labelRadius * Math.sin(angle);

    // Determine text-anchor based on position (left/right side of chart)
    let textAnchor: "start" | "middle" | "end" = "middle";
    const cosAngle = Math.cos(angle);
    if (cosAngle < -0.3)
      textAnchor = "end"; // Left side
    else if (cosAngle > 0.3) textAnchor = "start"; // Right side
    // Top/bottom stay "middle"

    return { label, x, y, textAnchor, value: values[index] ?? 0, angle };
  });

  // Calculate data point positions
  const dataPoints = values.map((value, index) => {
    const angle = index * angleStep - Math.PI / 2;
    const normalizedValue = value / 100;
    const x = centerX + radius * normalizedValue * Math.cos(angle);
    const y = centerY + radius * normalizedValue * Math.sin(angle);
    return { x, y };
  });

  // Determine SVG dimensions: responsive uses "100%" to fill parent, fixed uses pixel values
  const svgWidth = responsive ? "100%" : viewBoxWidth;
  const svgHeight = responsive ? "100%" : viewBoxHeight;

  return React.createElement(
    Svg,
    {
      width: svgWidth,
      height: svgHeight,
      viewBox: `0 0 ${viewBoxWidth} ${viewBoxHeight}`,
      // preserveAspectRatio maintains proportions when responsive
      preserveAspectRatio: "xMidYMid meet"
    },
    // No background - transparent
    // Grid lines
    React.createElement(
      G,
      { stroke: gridColor, strokeWidth: 1, fill: "none" },
      ...gridPaths.map((pathData, index) => React.createElement(Path, { key: `grid-${index}`, d: pathData }))
    ),
    // Auto-avaliação area (if present)
    hasAutoAvaliacao &&
      React.createElement(Path, {
        d: secondaryPathData,
        fill: secondaryColor,
        fillOpacity: 0.3,
        stroke: secondaryColor,
        strokeWidth: 2
      }),
    // Main data area
    React.createElement(Path, {
      d: primaryPathData,
      fill: primaryColor,
      fillOpacity: 0.3,
      stroke: primaryColor,
      strokeWidth: 2
    }),
    // Data points
    React.createElement(
      G,
      { fill: primaryColor },
      ...dataPoints.map((point, index) =>
        React.createElement(Circle, {
          key: `point-${index}`,
          cx: point.x,
          cy: point.y,
          r: 4
        })
      )
    ),
    // Labels
    showLabels &&
      React.createElement(
        G,
        {},
        ...labelPositions.flatMap((pos, posIndex) => {
          // Wrap text more aggressively for side labels
          const maxChars = pos.textAnchor === "middle" ? 14 : 10;
          const lines = wrapText(pos.label, maxChars);
          const lineHeight = fontSize * 1.4;

          // Adjust vertical position based on angle (top labels need different offset)
          const sinAngle = Math.sin(pos.angle);
          let yOffset = 0;
          if (sinAngle < -0.5)
            yOffset = -lineHeight; // Top labels: shift up
          else if (sinAngle > 0.5) yOffset = lineHeight * 0.5; // Bottom labels: shift down

          const startY = pos.y + yOffset - ((lines.length - 1) * lineHeight) / 2;

          return lines.map((line, lineIndex) => {
            const valueText = showValues && lineIndex === lines.length - 1 ? ` (${pos.value.toFixed(0)}%)` : "";
            const displayText = line + valueText;

            return React.createElement(
              SvgText,
              {
                key: `label-${posIndex}-${lineIndex}`,
                x: pos.x,
                y: startY + lineIndex * lineHeight,
                textAnchor: pos.textAnchor,
                style: {
                  fontSize,
                  fontFamily: "Helvetica",
                  fill: textColor
                }
              },
              displayText
            );
          });
        })
      )
  );
}
